import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from 'react-router-dom';

import { Layout } from "~components/UI";
import RefundsList from "./List";
import RefundsForm from "../DrawerRefunds";
import * as refundsActions from "./actions";
import { LoadingTopBar } from "~/components/UI/LoadingTopBar";
import { LocalCacheHandler } from '~/utils/local-cache-handler';
import { CACHE_ACTIONS } from '~/constants/cache-actions.constants';
import usePersistedFilters from '~/_shared/hooks/usePersistedFilters';
import {
  REFUNDS_FILTERS_KEY,
  getDefaultRefundsFilters,
  getRefundsRequest,
  isSameRefundsRequest,
} from './helpers';

export const ScreensRefunds = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const [isLoading, setIsLoading] = useState(true);
  const isFirstLoad = useRef(true);

  // Filters kept across refreshes for a few minutes (per tenant/user/page).
  const persisted = usePersistedFilters(REFUNDS_FILTERS_KEY, getDefaultRefundsFilters);
  const applied = persisted.filters;

  // Loads the restored (or default) filter on mount, and again whenever the
  // server-side filters (filter by, period, status) change.
  useEffect(() => {
    loadData();
  }, [applied.filterBy, applied.from, applied.to, applied.status]);

  async function loadData() {
    const firstLoad = isFirstLoad.current;
    isFirstLoad.current = false;
    // The IndexedDB cache is keyed by route only, so it only ever holds the
    // default period; using it for another filter would flash wrong data.
    const usesCache = isSameRefundsRequest(applied, persisted.defaults);

    try {
      if (firstLoad && usesCache) {
        await LocalCacheHandler.load({
          actionName: CACHE_ACTIONS.refunds,
          dispatch,
          pathname
        });
      }
      const request = getRefundsRequest(applied);
      // First load keeps the top bar only (as before); later changes use the default loader.
      if (firstLoad) request.showLoading = false;
      const { payload } = await dispatch(refundsActions.loadRefunds(request));
      if (usesCache) {
        await LocalCacheHandler.save({
          pathname,
          data: payload.data.payments
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div>
      <LoadingTopBar isActive={isLoading} />
      <Layout>
        <RefundsList persisted={persisted} />
        <RefundsForm />
      </Layout>
    </div>
  );
};
